import { AuthProvider } from 'react-admin';
import { DEVICE_LOCK_KEY } from './hooks/useDeviceLock';
import { Identity } from './interfaces/auth';
import { AuthService } from './services/auth';
import { HUB_AUTH_SOURCE, signOutHubSession } from './services/hubAuth';
import MixpanelService from './services/mixpanel';
import { clearMonitoredUser, identifyMonitoredUser, reportMonitoredError } from './services/sentry';
import { sessionMonitor } from './services/session';
import { StorageName } from './util/constants';

let logoutInFlight: Promise<void> | null = null;

/**
 * Everything up to the teardown await runs synchronously, so nothing another tab
 * writes in the meantime can be read or cleared by mistake.
 */
const endSession = async (): Promise<void> => {
  if (sessionMonitor.readStoredToken() && !sessionMonitor.ownsStoredSession()) {
    // Another tab has signed in since this one loaded its session. Ending that
    // newer session from a stale tab would sign the other tab out (and revoke
    // its credential on the Hub), so nothing is revoked or cleared: this tab
    // takes the stored session as its own instead. Resolving lets useLogout
    // push /login, whose checkAuth then sends the tab back to '/', remounting
    // CustomLayout and PhoneCallsProvider, which rebuilds a Twilio device torn
    // down on expiry. Returning false or '/' would leave that torn-down
    // provider mounted. The cost is that the page the user was on is lost.
    sessionMonitor.adoptStoredSession();
    return;
  }

  // Decided by the session this tab loaded, not by what storage holds now. With
  // no credential left, another tab has already signed it out.
  const { token, source } = sessionMonitor.getLoadedSession();
  const owned = sessionMonitor.ownsStoredSession();
  // Counted once, by the tab that ended the session
  if (owned) MixpanelService.track('Logout');
  // Takes the phone device offline on every forced logout too, not only the
  // guard's. Started before anything is cleared; shared with the guard's run.
  const tornDown = sessionMonitor.runTeardowns();
  // A Hub-minted session is ended on the Hub too; local state is cleared regardless
  if (owned && source === HUB_AUTH_SOURCE && token) signOutHubSession(token);
  localStorage.removeItem(StorageName.AUTH);
  localStorage.removeItem(StorageName.AUTH_SOURCE);
  localStorage.removeItem(StorageName.ROLES);
  // Was leaked: a stale Profile survived logout, so the next person to sign in
  // on this browser started with the previous user's identity until their own
  // profile fetch happened to succeed.
  localStorage.removeItem(StorageName.PROFILE);
  // Otherwise the next sign-in is told "device is active in another tab".
  localStorage.removeItem(DEVICE_LOCK_KEY);
  // Unconditional: Sentry's user is per page, so another tab's sign-out did not clear this one's
  clearMonitoredUser();
  sessionMonitor.reset();
  await tornDown;
};

/**
 * Every sign-out goes through `logout`, forced ones included: a checkAuth
 * rejection (react-admin's useCheckAuth), a checkError rejection
 * (useLogoutIfAccessDenied), the SessionExpirationGuard and the AppErrorBoundary.
 * So a Hub-minted session is ended on the Hub whichever of them ends it, not
 * only when the user clicks Logout.
 */
const authProvider: AuthProvider = {
  login: async (payload) => {
    const phone = payload.username;
    const password = payload.password;
    try {
      const response = await AuthService.signIn({ phone, password });
      if (response.status === 301) {
        throw new Error(
          'You are trying to log in as a provider. In order to login to your patient account, click on the www.enaradrive.com',
        );
      } else {
        AuthService.setRoles(response.data?.roles);
        // Stores the credential and clears any latched "dead" verdict from the
        // previous session, otherwise the guard would immediately sign the new
        // session out again. A password session has no Hub source and no expiry.
        sessionMonitor.startSession({ auth: response.data });

        // NOTE: Phone it is not a phone, it is the email :). Tech debt FTW
        identifyMonitoredUser(Number(response.data.user_id), phone);

        try {
          const profile = await AuthService.getProfile(response.data.auth_token);
          AuthService.setProfile(profile);
        } catch (e) {
          // Login still succeeds: we already hold a valid token, and consumers
          // now tolerate a missing profile. Not rethrown because the outer catch
          // would relabel it "Username or password is incorrect", and because
          // Core's /v1/profile/:token 500s when the User-Agent header is absent
          // — so a failure here does not reliably mean the token is bad.
          // Reported rather than console.log'd so it is actually visible.
          reportMonitoredError({ error: e });
        }

        MixpanelService.track('Login');
        return Promise.resolve();
      }
    } catch (err) {
      const response = (err as any).response;
      if (response && response.status === 403) {
        const { message } = response.data;
        throw new Error(message);
      }
      throw new Error(`Username or password is incorrect.`);
    }
  },
  logout: () => {
    // Several checkAuth rejections can each call logout in one navigation: one
    // sign-out, one Hub revoke, one 'Logout' event.
    if (!logoutInFlight) {
      logoutInFlight = endSession().finally(() => {
        logoutInFlight = null;
      });
    }
    return logoutInFlight;
  },
  checkError: async (error) => {
    // Axios nests the status under `response`. Reading `error.status` meant this
    // branch never matched a real HTTP failure.
    const status = error?.response?.status ?? error?.status;

    if (status === 401) {
      // A 401 for a token this tab no longer holds says nothing about its session
      if (!sessionMonitor.markRequestRejected(error, 'react-admin-check-error', { status })) {
        return Promise.resolve();
      }
      // Latched only with enforcement on, and then the guard owns the sign-out, as
      // in checkAuth: react-admin's logout would drop a live call the guard holds
      if (sessionMonitor.isSessionDead()) {
        return Promise.resolve();
      }
      return Promise.reject({ message: false });
    }

    // 403 is also ordinary RBAC, 404 is Core v1's rendering of a dead token, and
    // appointments-api answered 500 for one before it learned to say 401. All
    // ambiguous, so ask the server rather than guessing.
    if (status === 403 || status === 404 || (status >= 500 && status < 600)) {
      const verdict = await sessionMonitor.verifySession();
      if (verdict === 'dead' && !sessionMonitor.isSessionDead()) {
        return Promise.reject({ message: false });
      }
    }

    return Promise.resolve();
  },
  // `{ message: false }` is react-admin's documented "reject without showing a
  // notification" sentinel. A bare `Promise.reject()` rejects with `undefined`,
  // which Sentry reports as an unhelpful "Non-Error promise rejection captured
  // with value: undefined".
  checkAuth: async () => {
    if (!localStorage.getItem(StorageName.AUTH)) {
      return Promise.reject({ message: false });
    }
    if (sessionMonitor.isSessionDead()) {
      // Only latches with enforcement on, and then the SessionExpirationGuard owns
      // the sign-out: it waits for a live call to end, which a rejection here (and
      // react-admin's logout) would cut short.
      return;
    }

    const expiry = await sessionMonitor.checkExpiry();
    if (expiry === 'expired' && !sessionMonitor.isEnforcing()) {
      // Report-only: nothing latches, so this is what ends access past a
      // Core-confirmed expiry, or past the expiry ceiling
      return Promise.reject({ message: false });
    }
    if (expiry === 'not-due') {
      // Fire-and-forget on purpose. react-admin calls checkAuth on every route
      // change, so awaiting here would add a network round-trip to every click.
      // The monitor rate-limits this to at most one probe per 5 minutes.
      sessionMonitor.verifyIfStale();
    }
  },
  getPermissions: () => {
    const access = localStorage.getItem(StorageName.ROLES);
    return access ? Promise.resolve(access) : Promise.reject({ message: false });
  },
  getIdentity: () => {
    const jsonAuth = localStorage.getItem(StorageName.PROFILE);
    // Resolves rather than rejects when there is no usable profile. Callers such
    // as `src/components/rpms/note/Modal.tsx:61` await this without a try/catch
    // and guard on `identity?.fullName` immediately after, so rejecting here
    // would turn a missing profile into an unhandled rejection. A malformed
    // Profile previously threw synchronously; now it degrades the same way an
    // absent one does.
    if (!jsonAuth) {
      return Promise.resolve(null as unknown as Identity);
    }
    try {
      return Promise.resolve(JSON.parse(jsonAuth) as Identity);
    } catch (error) {
      return Promise.resolve(null as unknown as Identity);
    }
  },
};

export default authProvider;
